/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { round } from 'lodash'

import type { AccessorFn } from '@elastic/charts'

import type { CloudResource } from '@modules/billing-api/v1/types'

import type { BudgetConfigurationValues } from '../../../types'
import type { ChartData } from './types'

export const getCloudResourceMap = (
  billingCloudResources: CloudResource[] | undefined,
): Map<string, CloudResource> => {
  const map = new Map<string, CloudResource>()

  if (!billingCloudResources) {
    return map
  }

  billingCloudResources.forEach((resource) => {
    if (resource.id) {
      map.set(resource.id, resource)
    }
  })
  return map
}

export const getExistingScopeValues = (
  scopeValues: BudgetConfigurationValues['scopeValues'],
  cloudResourceMap: Map<string, CloudResource>,
): string[] => scopeValues?.filter((scopeValue) => cloudResourceMap.has(scopeValue)) || []

const SELECTED_OPTION_MAX_ID_CHARACTERS = 6

const _getInstanceDisplayName = (cloudResource: CloudResource) => {
  const resourceName = cloudResource.name
  const resourceIdSuffix = cloudResource.id.slice(-SELECTED_OPTION_MAX_ID_CHARACTERS)
  return resourceIdSuffix ? `${resourceName} (${resourceIdSuffix})` : resourceName
}

export const getAccessors = (
  scopeType: BudgetConfigurationValues['scopeType'],
  existingScopeValues: string[],
  cloudResourceMap: Map<string, CloudResource>,
): AccessorFn[] => {
  let accessors: AccessorFn[] = []

  switch (scopeType) {
    case 'organization':
      const organizationAccessor: AccessorFn = ({ values }) =>
        values?.reduce((sum, item) => sum + (item.value || 0), 0)
      organizationAccessor.fieldName = 'totalUsage'

      accessors = [organizationAccessor]
      break

    case 'cloud_resource':
      existingScopeValues.forEach((scopeValueId) => {
        const cloudResource = cloudResourceMap.get(scopeValueId)

        if (!cloudResource?.cluster_id) {
          return
        }

        const clusterId = cloudResource.cluster_id
        const displayName = _getInstanceDisplayName(cloudResource)

        const cloudResourceAccessor: AccessorFn = ({ values }) =>
          values
            .filter((value) => value.id === clusterId)
            .reduce((sum, item) => sum + (item.value || 0), 0)
        cloudResourceAccessor.fieldName = displayName

        accessors.push(cloudResourceAccessor)
      })
      break

    default:
      return []
  }

  return accessors
}

// Define custom yDomain based on the higher value between max data value and amount
export const getCustomYDomain = ({
  amountValue,
  usageChartData,
  yAccessors,
}: {
  amountValue: number
  usageChartData: ChartData[] | Array<{ timestamp: number; totalUsage: number }> | undefined
  yAccessors: Array<string | AccessorFn>
}) => {
  // Get the max value from chart data to ensure amount line is visible
  const maxDataValue = Math.max(
    ...(usageChartData?.map((item) =>
      yAccessors.reduce((sum, accessor) => {
        if (typeof accessor === 'string') {
          return sum + (Number(item[accessor]) || 0)
        }

        return sum + (Number(accessor(item)) || 0)
      }, 0),
    ) || []),
    0,
  )

  const customYDomain =
    amountValue > maxDataValue ? { min: 0, max: round(amountValue * 1.1, 2) } : undefined

  return customYDomain
}

export const getSeriesName = ({
  yAccessors,
  scopeType,
}: {
  yAccessors: Array<string | AccessorFn>
  scopeType: BudgetConfigurationValues['scopeType']
}) => {
  if (scopeType === 'organization') {
    return 'Total: '
  }

  if (yAccessors.length === 1 && typeof yAccessors[0] === 'function') {
    return yAccessors[0].fieldName
  }

  return undefined
}

export const getPrettyConsumptionValue = (value: number, precision: number) =>
  String(round(Math.abs(value), precision).toFixed(precision))
